import { Component } from '@theme/component';

/**
 * Horizontal card carousel with two layout modes.
 *
 * - Mobile: native CSS scroll-snap (touch swiping).
 * - Desktop (>= 750px): a stacked "deck" where the active card sits in front
 *   and the following cards peek out behind it, driven by transforms.
 *
 * The active index is shared between both modes so prev/next, dots, keyboard
 * and autoplay stay in sync when the viewport is resized.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} scroller - The track element.
 * @property {HTMLElement[]} [slides] - The card elements (`ref="slides[]"`).
 * @property {HTMLButtonElement[]} [dots] - The pagination dots (`ref="dots[]"`).
 *
 * @extends {Component<Refs>}
 */
export class CardCarousel extends Component {
  requiredRefs = ['scroller'];

  /** @type {number} Index of the active card. */
  #index = 0;

  /** @type {boolean} Whether the stacked (desktop) layout is active. */
  #stacked = false;

  /** @type {MediaQueryList | undefined} */
  #mql;

  /** @type {number | undefined} Autoplay timer id. */
  #timer;

  /** @type {number | undefined} rAF handle for the scroll listener. */
  #rafId;

  connectedCallback() {
    super.connectedCallback();

    this.#mql = window.matchMedia('(min-width: 750px)');
    this.#mql.addEventListener('change', this.#applyMode);
    this.refs.scroller?.addEventListener('scroll', this.#onScroll, { passive: true });

    this.#applyMode();
    this.#startAutoplay();

    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerup', this.#onPointerUp);
    this.addEventListener('pointercancel', this.#onPointerUp);
    this.addEventListener('pointerleave', this.#onPointerUp);

    this.addEventListener('pointerenter', this.#stopAutoplay);
    // Remove pointerleave from here since we use it above for drag cancel
    this.addEventListener('focusin', this.#stopAutoplay);
    this.addEventListener('focusout', this.#startAutoplay);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#mql?.removeEventListener('change', this.#applyMode);
    this.refs.scroller?.removeEventListener('scroll', this.#onScroll);
    this.#stopAutoplay();
  }

  /** @returns {number} Total number of cards. */
  get count() {
    return this.refs.slides?.length ?? 0;
  }

  /** @returns {boolean} Whether reduced motion is preferred. */
  get #reducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Advance to the next card (wraps to the first).
   * @param {Event} [event]
   */
  next = (event) => {
    event?.preventDefault();
    this.goTo(this.#index + 1);
  };

  /**
   * Go to the previous card (wraps to the last).
   * @param {Event} [event]
   */
  prev = (event) => {
    event?.preventDefault();
    this.goTo(this.#index - 1);
  };

  /**
   * Go to a specific card index, wrapping around both ends.
   * @param {number} target
   */
  goTo = (target) => {
    if (this.count === 0) return;

    this.#index = ((target % this.count) + this.count) % this.count;

    if (this.#stacked) {
      this.#applyStack();
    } else {
      this.#scrollToActive();
    }

    this.#updateDots();
    this.#updateLayout();
    this.#startAutoplay();
  };

  /**
   * Keyboard navigation from the host element.
   * @param {KeyboardEvent} event
   */
  handleKeydown = (event) => {
    if (event.key === 'ArrowRight') {
      this.next(event);
    } else if (event.key === 'ArrowLeft') {
      this.prev(event);
    }
  };

  /* ---------- Mode switching ---------- */

  #applyMode = () => {
    this.#stacked = this.#mql?.matches ?? false;
    this.classList.toggle('is-stacked', this.#stacked);

    if (this.#stacked) {
      this.#applyStack();
    } else {
      this.#resetTransforms();
      this.#scrollToActive('auto');
    }

    this.#updateDots();
    this.#updateLayout();
  };

  /* ---------- Stacked (desktop) layout ---------- */

  /** Position each card as a stacked deck relative to the active one. */
  #applyStack() {
    const { slides } = this.refs;
    if (!slides) return;

    const count = this.count;

    slides.forEach((card, i) => {
      // Forward distance from the active card (0 = front, then behind).
      const forward = (i - this.#index + count) % count;
      const isActive = forward === 0;

      let translateX = 0;
      let translateY = 0;
      let scale = 0.88;
      let opacity = 0;
      let z = 0;

      if (forward === 0) {
        translateX = 0;
        translateY = 0;
        scale = 1;
        opacity = 1;
        z = count + 1;
      } else if (forward <= 2) {
        translateX = forward * 9;
        translateY = forward * -2.5;
        scale = 1 - forward * 0.05;
        opacity = 1;
        z = count + 1 - forward;
      }

      card.style.transform = `translate(${translateX}%, ${translateY}%) scale(${scale})`;
      card.style.opacity = String(opacity);
      card.style.zIndex = String(z);
      card.style.pointerEvents = isActive ? 'auto' : 'none';
      card.setAttribute('aria-hidden', String(!isActive));
      card.classList.toggle('is-current', isActive);

      for (const focusable of card.querySelectorAll('a, button')) {
        if (focusable instanceof HTMLElement) {
          focusable.tabIndex = isActive ? 0 : -1;
        }
      }
    });
  }

  /** Clear inline styles applied by the stacked layout (back to scroll mode). */
  #resetTransforms() {
    const { slides } = this.refs;
    if (!slides) return;

    for (const card of slides) {
      card.style.transform = '';
      card.style.opacity = '';
      card.style.zIndex = '';
      card.style.pointerEvents = '';
      card.removeAttribute('aria-hidden');
      card.classList.remove('is-current');

      for (const focusable of card.querySelectorAll('a, button')) {
        if (focusable instanceof HTMLElement) {
          focusable.removeAttribute('tabindex');
        }
      }
    }
  }

  /* ---------- Scroll (mobile) layout ---------- */

  /**
   * Scroll the active card into the centre of the viewport.
   * @param {ScrollBehavior} [behavior]
   */
  #scrollToActive(behavior) {
    const { scroller, slides } = this.refs;
    if (!scroller || !slides) return;

    const card = slides[this.#index];
    if (!card) return;

    const left = card.offsetLeft - (scroller.clientWidth - card.clientWidth) / 2;
    scroller.scrollTo({
      left,
      behavior: behavior ?? (this.#reducedMotion ? 'auto' : 'smooth'),
    });
  }

  #onScroll = () => {
    if (this.#stacked || this.#rafId) return;
    this.#rafId = window.requestAnimationFrame(() => {
      this.#rafId = undefined;
      this.#syncActive();
    });
  };

  /** Derive the active index from scroll position (scroll mode only). */
  #syncActive() {
    const { scroller, slides } = this.refs;
    if (!scroller || !slides) return;

    const center = scroller.scrollLeft + scroller.clientWidth / 2;
    let closest = 0;
    let min = Infinity;

    slides.forEach((card, i) => {
      const cardCenter = card.offsetLeft + card.clientWidth / 2;
      const distance = Math.abs(cardCenter - center);
      if (distance < min) {
        min = distance;
        closest = i;
      }
    });

    if (closest !== this.#index) {
      this.#index = closest;
      this.#updateDots();
      this.#updateLayout();
    }
  }

  /** Mirror the visible arrow to the active card's content side (mobile). */
  #updateLayout() {
    const active = this.refs.slides?.[this.#index];
    const isRight = active instanceof HTMLElement && active.dataset.layout === 'right';
    this.classList.toggle('is-content-right', isRight);
  }

  #updateDots() {
    const { dots } = this.refs;
    if (!dots) return;

    dots.forEach((dot, i) => {
      const current = i === this.#index;
      dot.classList.toggle('is-active', current);
      dot.setAttribute('aria-current', String(current));
    });
  }

  /* ---------- Pointer dragging ---------- */

  #dragging = false;
  #startX = 0;
  #deltaX = 0;

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.button !== 0 || this.#stacked) return;
    this.#dragging = true;
    this.#startX = event.clientX;
    this.#deltaX = 0;
    this.#stopAutoplay();
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (!this.#dragging) return;
    this.#deltaX = event.clientX - this.#startX;
  };

  /** @param {PointerEvent} _event */
  #onPointerUp = (_event) => {
    if (!this.#dragging) {
      if (_event.type === 'pointerleave') this.#startAutoplay();
      return;
    }
    this.#dragging = false;

    const threshold = 40;
    if (this.#deltaX <= -threshold) {
      this.next();
    } else if (this.#deltaX >= threshold) {
      this.prev();
    } else {
      this.#startAutoplay();
    }
    this.#deltaX = 0;
  };

  /* ---------- Autoplay ---------- */

  #startAutoplay = () => {
    this.#stopAutoplay();

    const speed = Number(this.getAttribute('data-autoplay'));
    if (!speed || this.#reducedMotion || this.count < 2) return;

    this.#timer = window.setInterval(() => this.next(), speed);
  };

  #stopAutoplay = () => {
    if (this.#timer) {
      window.clearInterval(this.#timer);
      this.#timer = undefined;
    }
  };
}

if (!customElements.get('o28-card-carousel')) {
  customElements.define('o28-card-carousel', CardCarousel);
}
